<template>
	<div class="custom-dashboard-v2">
		<!-- Header with Filters and Back Button -->
		<title-header class="dashboard-header">
			<v-layout row wrap justify-space-between align-center class="header-layout">
				<v-layout row wrap justify-start align-center class="filters-layout">
					<v-flex xs6 sm4 md3 class="filter-item">
						<label class="filter-label" :style="`color:${$appColors.success}`">
							Área
							<v-select
								label="Área"
								class="select-core select-compact"
								hide-details
								:menu-props="{ contentClass: 'type-select-content' }"
								height="28"
								autocomplete
								clearable
								multiple
								outline
								single-line
								dense
								:items="groups"
								item-text="name"
								item-value="id"
								return-object
								@input="getFilter('group', $event)"
							></v-select>
						</label>
					</v-flex>
					<v-flex xs6 sm4 md3 class="filter-item">
						<label class="filter-label" :style="`color:${$appColors.success}`">
							Agencia
							<v-select
								label="Agencia"
								class="select-core select-compact"
								hide-details
								:menu-props="{ contentClass: 'type-select-content' }"
								height="28"
								autocomplete
								clearable
								multiple
								outline
								single-line
								dense
								:items="subsidiaries"
								item-text="name"
								item-value="id"
								return-object
								@input="getFilter('subsidiary', $event)"
							></v-select>
						</label>
					</v-flex>
				</v-layout>
				<div class="wrapper-btns">
					<v-btn color="primary btn-action" @click="goToBack" class="back-btn" small>
						<img
							src="/static/img/icons/btn-right-arrow.webp"
							alt="Atrás"
							class="back-btn-icon"
						/>
						<span class="back-btn-text">Atrás</span>
					</v-btn>
				</div>
			</v-layout>
		</title-header>

		<!-- KPI Cards Section -->
		<section class="kpi-section">
			<div class="kpi-grid">
				<!-- Evaluadores Card -->
				<div class="kpi-card kpi-card--primary">
					<div class="kpi-card__header">
						<div class="kpi-card__icon">
							<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-1.79 4-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
							</svg>
						</div>
						<div class="kpi-card__trend" v-if="evaluatorTrend > 0">
							<span class="trend-indicator trend-indicator--up">+{{ evaluatorTrend }}%</span>
						</div>
					</div>
					<div class="kpi-card__content">
						<p class="kpi-card__label">Evaluadores</p>
						<p class="kpi-card__value">{{ evaluators.length }}</p>
						<div class="kpi-card__progress" :style="{ width: evaluatorProgress + '%' }"></div>
					</div>
				</div>

				<!-- Colaboradores Card -->
				<div class="kpi-card kpi-card--success">
					<div class="kpi-card__header">
						<div class="kpi-card__icon">
							<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
							</svg>
						</div>
					</div>
					<div class="kpi-card__content">
						<p class="kpi-card__label">Colaboradores</p>
						<p class="kpi-card__value">{{ colaborators.length }}</p>
						<div class="kpi-card__progress kpi-card__progress--success" :style="{ width: colaboratorProgress + '%' }"></div>
					</div>
				</div>

				<!-- Gender Distribution - Donut Chart -->
				<div class="kpi-card">
					<div class="kpi-card__header">
						<p class="kpi-card__label-inline">Distribución por Género</p>
					</div>
					<div class="kpi-card__donut">
						<apexchart
							type="donut"
							height="160"
							:options="genderDonutOptions"
							:series="genderDonutSeries"
						></apexchart>
						<div class="donut-legend">
							<div class="legend-item">
								<span class="legend-dot legend-dot--male"></span>
								<span class="legend-label">Hombres {{ maleFemale.percentMale || 0 }}%</span>
							</div>
							<div class="legend-item">
								<span class="legend-dot legend-dot--female"></span>
								<span class="legend-label">Mujeres {{ maleFemale.percentFemale || 0 }}%</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Charts Grid -->
		<section class="charts-grid">
			<!-- Main Competencies Chart - Vertical Bar -->
			<div class="chart-card chart-card--main">
				<div class="chart-card__header chart-card__header--compact">
					<h3 class="chart-card__title">Competencias Principales</h3>
					<div class="chart-card__actions">
						<button class="btn-icon" @click="expandCompetenceChart" aria-label="Ver gráfico expandido">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
							</svg>
						</button>
						<button v-if="false" class="btn-icon" @click="refreshData" :disabled="loading" aria-label="Actualizar datos">
							<svg v-if="!loading" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M17.65 6.35A8 8 0 1 0 12.05 2.65M21 12a9 9 0 1 1-9-9m0 9v9m9-9h-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							<svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="spin">
								<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-dasharray="32" stroke-linecap="round"/>
							</svg>
						</button>
					</div>
				</div>
				<div class="chart-card__body">
					<apexchart
						type="bar"
						:height="competenceChartHeight"
						:options="competenceChartOptions"
						:series="competenceChartSeries"
					></apexchart>
				</div>
			</div>

			<!-- Jobs Chart - Horizontal Bar -->
			<div class="chart-card chart-card--main" v-if="hasJobsData">
				<div class="chart-card__header chart-card__header--compact">
					<h3 class="chart-card__title">Puestos de Trabajo</h3>
					<div class="chart-card__actions">
						<button class="btn-icon" @click="expandJobsChart" aria-label="Ver gráfico expandido">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
							</svg>
						</button>
						<button v-if="false" class="btn-icon" @click="refreshData" :disabled="loading" aria-label="Actualizar datos">
							<svg v-if="!loading" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M17.65 6.35A8 8 0 1 0 12.05 2.65M21 12a9 9 0 1 1-9-9m0 9v9m9-9h-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							<svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="spin">
								<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-dasharray="32" stroke-linecap="round"/>
							</svg>
						</button>
					</div>
				</div>
				<div class="chart-card__body chart-card__body--full">
					<apexchart
						type="bar"
						:height="jobsChartHeight"
						:options="jobsChartOptions"
						:series="jobsChartSeries"
					></apexchart>
				</div>
			</div>

			<!-- Jobs Chart Empty State -->
			<div class="chart-card chart-card--main" v-if="!hasJobsData && jobsInProcess && Object.keys(jobsInProcess).length > 0">
				<div class="chart-card__header chart-card__header--compact">
					<h3 class="chart-card__title">Puestos de Trabajo</h3>
				</div>
				<div class="chart-card__body chart-card__body--empty">
					<svg class="empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#cbd5e1"/>
					</svg>
					<p class="empty-text">Sin datos de puestos</p>
					<p class="empty-subtext">No se encontraron puestos de trabajo para mostrar</p>
				</div>
			</div>

			<!-- Distribution by Alternative - Stacked Bar -->
			<div class="chart-card chart-card--wide" v-if="hasAlternativesDistribution">
				<div class="chart-card__header chart-card__header--compact">
					<h3 class="chart-card__title">Distribución por Nivel de Desarrollo</h3>
					<div class="chart-card__actions">
						<button class="btn-icon" @click="expandAlternativesChart" aria-label="Ver gráfico expandido">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
							</svg>
						</button>
					</div>
				</div>
				<div class="chart-card__body">
					<apexchart
						type="bar"
						height="300"
						:options="alternativesDistributionOptions"
						:series="alternativesDistributionSeries"
					></apexchart>
				</div>
			</div>
		</section>

		<!-- Radar/Progress by Group -->
		<section class="radar-section" v-if="hasRadarData">
			<div class="section-header">
				<h2 class="section-title">Evaluación por Grupo</h2>
				<p class="section-subtitle">Desglose de resultados por grupo de evaluación</p>
			</div>
			<div class="radar-grid">
				<div
					v-for="(group, index) in enhancedRadarData"
					:key="'group-' + index"
					class="radar-card"
				>
					<div class="radar-card__header">
						<h4 class="radar-card__title">{{ group.name }}</h4>
						<div class="radar-card__actions">
							<button class="btn-icon" @click="expandRadarChart(group)" aria-label="Ver gráfico expandido">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
								</svg>
							</button>
						</div>
						<span class="radar-card__badge">{{ group.totalEvaluations }} evaluaciones</span>
					</div>
					<div class="radar-card__body">
						<apexchart
							type="radialBar"
							height="280"
							:options="group.radialOptions"
							:series="group.radialSeries"
						></apexchart>
					</div>
					<div class="radar-card__footer">
						<div
							v-for="(item, idx) in group.topCompetencies"
							:key="'comp-' + idx"
							class="competency-item"
						>
							<span class="competency-item__label">{{ item.name }}</span>
							<span class="competency-item__value">{{ Number(item.value).toFixed(2) }}%</span>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Radar Empty State -->
		<section class="radar-section" v-if="!hasRadarData && dataRadarChart && dataRadarChart.length > 0">
			<div class="section-header">
				<h2 class="section-title">Evaluación por Grupo</h2>
				<p class="section-subtitle">Desglose de resultados por grupo de evaluación</p>
			</div>
			<div class="radar-grid">
				<div class="radar-card radar-card--full">
					<div class="chart-card__body chart-card__body--empty">
						<svg class="empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#cbd5e1"/>
						</svg>
						<p class="empty-text">Sin datos para mostrar</p>
						<p class="empty-subtext">No se encontraron resultados de evaluación para los grupos disponibles</p>
					</div>
				</div>
			</div>
		</section>

		<!-- NEW: Box Plot - Statistical Analysis -->
		<section class="stats-section" v-if="hasBoxPlotData">
			<div class="section-header">
				<h2 class="section-title">Análisis Estadístico</h2>
				<p class="section-subtitle">Distribución de puntuaciones por competencia (Box Plot)</p>
			</div>
			<div class="charts-grid">
				<div class="chart-card chart-card--full">
					<div class="chart-card__header">
						<h3 class="chart-card__title">Distribución por Cuartiles</h3>
						<div class="chart-card__actions">
							<button class="btn-icon" @click="expandBoxPlotChart" aria-label="Ver gráfico expandido">
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
								</svg>
							</button>
						</div>
						<span class="chart-card__badge">Min | Q1 | Mediana | Q3 | Max</span>
					</div>
					<div class="chart-card__body">
						<apexchart
							type="boxPlot"
							height="400"
							:options="boxPlotOptions"
							:series="boxPlotData.series"
							:key="'boxplot-' + (boxPlotData.categories && boxPlotData.categories.length)"
						></apexchart>
					</div>
				</div>
			</div>
		</section>

		<!-- No Data State for Advanced Charts -->
		<section class="no-data-section" v-if="!hasBoxPlotData && dataEvaluation && dataEvaluation.length > 0">
			<div class="charts-grid">
				<div class="chart-card chart-card--full">
					<div class="chart-card__body chart-card__body--empty">
						<svg class="empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#cbd5e1"/>
						</svg>
						<p class="empty-text">Datos insuficientes para gráficos estadísticos</p>
						<p class="empty-subtext">Se requieren al menos 2 evaluaciones por competencia para generar análisis estadísticos</p>
					</div>
				</div>
			</div>
		</section>

		<!-- NEW: Individual Competencies Section -->
		<section class="competencies-section" v-if="hasDataGraphics && validDataGraphics.length > 0">
			<div class="section-header">
				<h2 class="section-title">Competencias Individuales</h2>
				<p class="section-subtitle">Análisis detallado por competencia con vistas expandidas</p>
			</div>
			<div class="competencies-grid">
				<div
					v-for="(graphic, index) in validDataGraphics"
					:key="graphic.type + '-' + (graphic.categories && graphic.categories.length) + '-' + index"
					class="competency-card"
				>
					<card-dashboard
						:data-competences="graphic"
						title="Competencia"
						:height="450"
						@show-graphic="showGraphicModal($event)"
					></card-dashboard>
				</div>
			</div>
		</section>

		<!-- Competencies Empty State -->
		<section class="competencies-section" v-if="hasDataGraphics && validDataGraphics.length === 0">
			<div class="section-header">
				<h2 class="section-title">Competencias Individuales</h2>
				<p class="section-subtitle">Análisis detallado por competencia con vistas expandidas</p>
			</div>
			<div class="competencies-grid">
				<div class="competency-card competency-card--full">
					<div class="chart-card__body chart-card__body--empty">
						<svg class="empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#cbd5e1"/>
						</svg>
						<p class="empty-text">Sin datos para mostrar</p>
						<p class="empty-subtext">No se encontraron datos válidos para las competencias individuales</p>
					</div>
				</div>
			</div>
		</section>

		<!-- Modal for Individual Graphics - Extra Large Dimensions -->
		<v-dialog
			:value="graphicModel"
			@input="graphicModel = $event"
			width="96%"
			:attach="true"
			content-class="graphic-modal-premium"
			persistent
		>
			<p-card class="graphic-modal-card-premium">
				<p-header-dialog
					:negative-margin="false"
					@closeClicked="closeModal()"
					class="graphic-modal-header"
				>
					{{ graphicModalTitle }}
				</p-header-dialog>
				<div class="graphic-modal-body-premium">
					<card-dashboard-individual
						v-if="Object.keys(dataGraphicByModal).length !== 0"
						:key="modalChartHeight"
						:data-competences="dataGraphicByModal"
						:predefinedOptions="dataGraphicByModal.options"
						:height="modalChartHeight"
						:view="true"
						title=""
						@show-graphic="showGraphicModal($event)"
					></card-dashboard-individual>
				</div>
			</p-card>
		</v-dialog>
	</div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import CardDashboard from '@/components/dashboard/card-dashboard';
import CardDashboardIndividual from '@/components/dashboard/card-dashboard-individual';
import PCard from '@/modules/shared/components/ui/Card/p-card';
import PHeaderDialog from '@/modules/shared/components/ui/dialog/p-header-dialog';
import titleHeader from '@/components/diagnosis/title-header';
import {
	calculateQuartiles,
	calculateMinMax,
} from '@/shared/statistics-utils';
import {
	CHART_COLORS,
} from '@/shared/chart-colors';
import helper from '@/shared/helper';
import bus from '@/bus/app-bus';
import { MAIN_LOADER_EVENT } from '@/modules/shared/components/ui/p-main-loader.constants';

const { roundTo } = helper;

export default {
	name: 'custom-dashboard-v2',
	components: {
		apexchart: VueApexCharts,
		'card-dashboard': CardDashboard,
		'card-dashboard-individual': CardDashboardIndividual,
		PCard,
		PHeaderDialog,
		'title-header': titleHeader,
	},
	props: {
		processId: {
			type: [String, Number],
			default: null,
		},
		pollId: {
			type: [String, Number],
			default: null,
		},
	},
	data() {
		return {
			loading: false,
			graphicModel: false,
			dataGraphicByModal: {},
			modalChartHeight: 450,
			isModalOpen: false,
			evaluators: [],
			colaborators: [],
			maleFemale: {
				male: [],
				female: [],
				percentMale: 0,
				percentFemale: 0,
			},
			totalCompetences: {},
			dataGraphics: [],
			jobsInProcess: {},
			dataRadarChart: [],
			dataEvaluation: [],
			boxPlotDataInternal: { series: [], categories: [], hasData: false },
			groups: [],
			subsidiaries: [],
			groupIds: [],
			subsidiaryIds: [],
		};
	},
	computed: {
		genderDonutSeries() {
			return [this.maleFemale.percentMale || 0, this.maleFemale.percentFemale || 0];
		},
		genderDonutOptions() {
			return {
				chart: {
					type: 'donut',
					fontFamily: 'inherit',
				},
				colors: ['#f09c2b', '#ec4899'],
				labels: ['Hombres', 'Mujeres'],
				legend: {
					show: false,
				},
				plotOptions: {
					pie: {
						donut: {
							size: '70%',
							labels: {
								show: false,
							},
						},
					},
				},
				dataLabels: {
					enabled: false,
				},
				stroke: {
					show: false,
				},
				tooltip: {
					y: {
						formatter: val => `${val}%`,
					},
				},
			};
		},
		evaluatorProgress() {
			const colaboratorCount = Math.max(this.colaborators.length, 1);
			return Math.min((this.evaluators.length / colaboratorCount) * 100, 100);
		},
		colaboratorProgress() {
			return Math.min((this.colaborators.length / 100) * 100, 100);
		},
		evaluatorTrend() {
			// Simulated trend - in real app would compare with previous period
			return this.evaluators.length > 0 ? 12 : 0;
		},
		competenceChartSeries() {
			if (!this.totalCompetences || !this.totalCompetences.series) {
				return [{ name: 'Competencias', data: [] }];
			}
			const validated = this.totalCompetences.series.map(serie => ({
				name: serie.name || 'Competencia',
				data: Array.isArray(serie.data) ? serie.data : [],
			}));
			if (validated.length === 0) {
				return [{ name: 'Competencias', data: [] }];
			}
			return validated;
		},
		competenceChartHeight() {
			if (!this.totalCompetences || !this.totalCompetences.categories) {
				return 380;
			}
			const categoryCount = this.totalCompetences.categories.length;
			if (categoryCount <= 4) {
				return 320;
			}
			if (categoryCount <= 8) {
				return 380;
			}
			return 450;
		},
		competenceChartMaxValue() {
			if (!this.totalCompetences || !this.totalCompetences.series) {
				return 100;
			}
			let maxVal = 0;
			this.totalCompetences.series.forEach((serie) => {
				if (serie.data && Array.isArray(serie.data)) {
					serie.data.forEach((val) => {
						if (typeof val === 'number' && val > maxVal) {
							maxVal = val;
						}
					});
				}
			});
			if (maxVal === 0) {
				return 100;
			}
			return Math.ceil(maxVal * 1.2);
		},
		competenceChartOptions() {
			const categories = this.totalCompetences &&
				this.totalCompetences.categories &&
				Array.isArray(this.totalCompetences.categories)
				? this.totalCompetences.categories
				: [];
			const maxValue = this.competenceChartMaxValue;

			return {
				chart: {
					type: 'bar',
					fontFamily: 'inherit',
					toolbar: { show: false },
					parentHeightOffset: 0,
					background: 'transparent',
				},
				plotOptions: {
					bar: {
						horizontal: false,
						borderRadius: 8,
						borderRadiusApplication: 'end',
						columnWidth: '60%',
						distributed: true,
					},
				},
				colors: CHART_COLORS.multiSeries,
				dataLabels: {
					enabled: false,
				},
				xaxis: {
					categories,
					labels: {
						show: true,
						rotate: -45,
						trim: true,
						maxHeight: 80,
						hideOverlappingLabels: false,
						style: {
							fontSize: '10px',
							fontWeight: 600,
							colors: ['#334155'],
							fontFamily: 'inherit',
						},
					},
					axisBorder: { show: false },
					axisTicks: { show: false },
					tooltip: { enabled: false },
				},
				yaxis: {
					max: maxValue,
					labels: {
						formatter: function formatterY(val) {
							if (typeof val === 'number' && val >= 1000) {
								return `${(val / 1000).toFixed(1)}k`;
							}
							return val;
						},
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: ['#64748b'],
							fontFamily: 'inherit',
						},
					},
					title: {
						text: 'Valor',
						style: {
							fontSize: '11px',
							fontWeight: 600,
							colors: ['#94a3b8'],
							fontFamily: 'inherit',
						},
					},
				},
				grid: {
					show: true,
					borderColor: '#f1f5f9',
					strokeDashArray: 0,
					position: 'back',
				},
				tooltip: {
					enabled: true,
					theme: 'light',
					style: { fontSize: '12px', fontFamily: 'inherit' },
					y: {
						formatter: function formatterY(val) {
							const value = typeof val === 'number' ? val : 0;
							return `
								<div style="text-align: center;">
									<div style="font-size: 18px; font-weight: 700; color: #55ac4b;">${value}</div>
								</div>
							`;
						},
						title: { formatter: function formatterTitle() { return 'Valor'; } },
					},
					x: {
						formatter: function formatterX(val, { dataPointIndex }) {
							const competenceName = categories && categories[dataPointIndex] !== undefined
								? categories[dataPointIndex]
								: '';
							return `
								<div style="text-align: center;">
									<div style="font-size: 11px; font-weight: 600; color: #1e293b;">${competenceName}</div>
								</div>
							`;
						},
					},
					marker: { show: false },
				},
				legend: { show: false },
			};
		},
		jobsChartSeries() {
			if (!this.jobsInProcess || !this.jobsInProcess.series) {
				return [{ name: 'Puestos', data: [] }];
			}
			// Validar que cada serie tenga la propiedad data
			const validated = this.jobsInProcess.series.map(serie => ({
				name: serie.name || 'Puestos',
				data: Array.isArray(serie.data) ? serie.data : [],
			}));
			// Si no hay series válidas, retornar una estructura vacía
			if (validated.length === 0) {
				return [{ name: 'Puestos', data: [] }];
			}
			return validated;
		},
		hasJobsData() {
			// Verificar si hay datos válidos de puestos de trabajo
			if (!this.jobsInProcess || Object.keys(this.jobsInProcess).length === 0) {
				return false;
			}
			// Verificar que haya series y categorías con datos
			const hasSeries = this.jobsInProcess.series &&
				this.jobsInProcess.series.length > 0 &&
				this.jobsInProcess.series[0].data &&
				this.jobsInProcess.series[0].data.length > 0;
			const hasCategories = this.jobsInProcess.categories &&
				this.jobsInProcess.categories.length > 0;
			return hasSeries && hasCategories;
		},
		jobsChartHeight() {
			if (!this.jobsInProcess || !this.jobsInProcess.categories) {
				return 380;
			}
			const categoryCount = this.jobsInProcess.categories.length;
			const baseOffset = 60;
			const heightPerBar = 50;
			const calculatedHeight = baseOffset + (categoryCount * heightPerBar);
			const minHeight = 250;
			const maxHeight = 800;
			// Match the competence chart height for visual balance
			const targetHeight = categoryCount <= 5 ? 380 : calculatedHeight;
			return Math.min(Math.max(targetHeight, minHeight), maxHeight);
		},
		jobsChartTruncatedLabels() {
			if (!this.jobsInProcess || !this.jobsInProcess.categories) {
				return [];
			}
			const maxLabelLength = 40;
			return this.jobsInProcess.categories.map((category) => {
				if (typeof category !== 'string') {
					return String(category);
				}
				return category.length > maxLabelLength
					? `${category.substring(0, maxLabelLength - 3)}...`
					: category;
			});
		},
		jobsChartOptions() {
			const categories = this.jobsInProcess &&
				this.jobsInProcess.categories &&
				Array.isArray(this.jobsInProcess.categories)
				? this.jobsInProcess.categories
				: [];

			return {
				chart: {
					type: 'bar',
					fontFamily: 'inherit',
					toolbar: { show: false },
					parentHeightOffset: 0,
				},
				plotOptions: {
					bar: {
						borderRadius: 8,
						borderRadiusApplication: 'end',
						horizontal: true,
						barHeight: '75%',
					},
				},
				colors: [CHART_COLORS.primary],
				dataLabels: {
					enabled: false,
				},
				xaxis: {
					categories,
					labels: { show: false },
					axisBorder: { show: false },
					axisTicks: { show: false },
					tooltip: { enabled: false },
				},
				yaxis: {
					labels: {
						show: true,
						style: {
							fontSize: '9px',
							fontWeight: 500,
							colors: ['#334155'],
							fontFamily: 'inherit',
						},
						minWidth: 0,
						maxWidth: 300,
						offsetX: 0,
						offsetY: 0,
						trim: false,
					},
				},
				grid: {
					show: false,
					padding: { left: 0, right: 0, top: 0, bottom: 0 },
				},
				tooltip: {
					enabled: true,
					theme: 'light',
					style: { fontSize: '12px', fontFamily: 'inherit' },
					y: {
						formatter: function formatterY(val, { dataPointIndex }) {
							const jobName = categories && categories[dataPointIndex] !== undefined
								? categories[dataPointIndex]
								: '';
							const count = typeof val === 'number' ? val : 0;
							return `${jobName}: ${count}`;
						},
						title: { formatter: function formatterTitle() { return ''; } },
					},
				},
				legend: { show: false },
			};
		},
		// ============================================================
		// DISTRIBUTION BY ALTERNATIVE (Development Levels)
		// ============================================================
		hasAlternativesDistribution() {
			if (!this.dataGraphics || this.dataGraphics.length < 2) {
				return false;
			}
			const alternativesData = this.dataGraphics[1];
			return alternativesData &&
				alternativesData.series &&
				Array.isArray(alternativesData.series) &&
				alternativesData.series.length > 0;
		},
		alternativesDistributionSeries() {
			if (!this.hasAlternativesDistribution) {
				return [];
			}
			const alternativesData = this.dataGraphics[1];
			// Validar y limpiar cada serie
			const validated = alternativesData.series
				.map((serie) => {
					if (!serie || !serie.data || !Array.isArray(serie.data)) {
						return null;
					}
					const cleanData = serie.data.filter(val =>
						typeof val === 'number' && !isNaN(val) && isFinite(val) && val >= 0,
					);
					if (cleanData.length === 0) {
						return null;
					}
					return {
						name: serie.name || 'Nivel',
						data: cleanData,
					};
				})
				.filter(s => s !== null);
			return Array.isArray(validated) && validated.length > 0 ? validated : [];
		},
		alternativesDistributionOptions() {
			if (!this.hasAlternativesDistribution) {
				return {};
			}
			const alternativesData = this.dataGraphics[1];
			const truncateLabel = (label) => {
				if (label.length > 20) {
					return `${label.substring(0, 17)}...`;
				}
				return label;
			};
			const categories = alternativesData && alternativesData.categories
				? alternativesData.categories.map(truncateLabel)
				: [];

			return {
				chart: {
					type: 'bar',
					height: 300,
					fontFamily: 'inherit',
					toolbar: { show: false },
					background: 'transparent',
					stacked: true,
				},
				plotOptions: {
					bar: {
						horizontal: false,
						borderRadius: 6,
						borderRadiusApplication: 'end',
						columnWidth: '70%',
					},
				},
				colors: ['#22c55e', '#84cc16', '#f59e0b', '#ef4444'],
				dataLabels: {
					enabled: false,
				},
				xaxis: {
					categories,
					labels: {
						rotate: -45,
						trim: true,
						maxHeight: 70,
						hideOverlappingLabels: false,
						style: {
							fontSize: '9px',
							fontFamily: 'inherit',
							fontWeight: 500,
							colors: CHART_COLORS.info,
						},
					},
					tooltip: { enabled: false },
				},
				yaxis: {
					labels: {
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: CHART_COLORS.info,
						},
					},
					title: {
						text: 'Cantidad',
						style: {
							fontSize: '11px',
							fontWeight: 600,
							colors: CHART_COLORS.info,
						},
					},
				},
				grid: {
					borderColor: '#f1f5f9',
				},
				tooltip: {
					theme: 'light',
					style: {
						fontSize: '11px',
						fontFamily: 'inherit',
					},
					y: {
						formatter: val => `${Number(val)} evaluaciones`,
					},
				},
				legend: {
					show: true,
					position: 'bottom',
					fontSize: '10px',
					fontWeight: 600,
					itemMargin: {
						horizontal: 8,
						vertical: 4,
					},
				},
				fill: {
					opacity: 0.9,
				},
			};
		},
		enhancedRadarData() {
			if (!this.dataRadarChart || this.dataRadarChart.length === 0) {
				return [];
			}

			return this.dataRadarChart.map((group) => {
				// Validar que el grupo tenga opciones válidas
				if (!group.options || group.options.length === 0) {
					return null;
				}

				const validOptions = group.options.filter(opt =>
					opt.series &&
					Array.isArray(opt.series) &&
					opt.series.length > 0 &&
					typeof opt.series[0] === 'number' &&
					opt.series[0] >= 0 &&
					isFinite(opt.series[0]));

				if (validOptions.length === 0) {
					return null;
				}

				// Asegurar que radialSeries sea un array de números válidos puros
				const radialSeries = validOptions
					.map((opt) => {
						const val = opt.series[0];
						return typeof val === 'number' && !isNaN(val) && isFinite(val) && val >= 0 ? val : 0;
					})
					.slice(0, 4);

				// Si no hay valores válidos, retornar null
				if (radialSeries.length === 0 || radialSeries.every(v => v === 0)) {
					return null;
				}

				const radialOptions = {
					chart: {
						type: 'radialBar',
						fontFamily: 'inherit',
						toolbar: { show: false },
					},
					plotOptions: {
						radialBar: {
							hollow: {
								size: '55%',
							},
							track: {
								background: '#f1f5f9',
								strokeWidth: '97%',
							},
							dataLabels: {
								name: {
									show: true,
									fontSize: '10px',
									color: '#64748b',
									fontWeight: 600,
								},
								value: {
									show: true,
									fontSize: '13px',
									fontWeight: 700,
									color: '#0f172a',
									formatter: val => `${Number(val).toFixed(2)}%`,
								},
							},
						},
					},
					colors: CHART_COLORS.multiSeries.slice(0, 4),
					labels: validOptions.slice(0, 4).map((opt) => {
						// Truncar nombres largos
						const name = opt.alternative || 'N/A';
						return name.length > 15 ? `${name.substring(0, 12)}...` : name;
					}),
					legend: {
						show: true,
						position: 'bottom',
						fontSize: '9px',
						fontWeight: 600,
						itemMargin: {
							horizontal: 5,
							vertical: 2,
						},
					},
				};

				// Get top competencies - mostrar las mismas 4 que el gráfico
				const topCompetencies = validOptions
					.slice(0, 4)
					.map(opt => ({
						name: opt.alternative,
						value: (opt.series && opt.series[0] && typeof opt.series[0] === 'number' && !isNaN(opt.series[0]))
							? opt.series[0]
							: 0,
					}))
					.filter(c => c.value >= 0); // Incluir valores >= 0 para mostrar todos

				return {
					name: group.name,
					radialSeries,
					radialOptions,
					totalEvaluations: group.total || 0,
					topCompetencies,
				};
			}).filter(group => group !== null);
		},
		hasRadarData() {
			return this.enhancedRadarData.length > 0;
		},
		// ============================================================
		// NEW: Box Plot Chart - Distribución Estadística
		// ============================================================
		boxPlotData() {
			// Retornar el valor calculado en loadData()
			return this.boxPlotDataInternal || { series: [], categories: [], hasData: false };
		},
		hasBoxPlotData() {
			return (this.boxPlotDataInternal && this.boxPlotDataInternal.hasData) || false;
		},
		boxPlotYAxisRange() {
			if (
				!this.boxPlotDataInternal ||
				!this.boxPlotDataInternal.series ||
				!this.boxPlotDataInternal.series[0]
			) {
				return { min: 0, max: 100 };
			}
			const data = this.boxPlotDataInternal.series[0].data;
			if (!Array.isArray(data) || data.length === 0) {
				return { min: 0, max: 100 };
			}

			// Encontrar el rango real de todos los datos
			let allValues = [];
			data.forEach((point) => {
				if (point.y && Array.isArray(point.y)) {
					allValues = allValues.concat(point.y);
				}
			});

			if (allValues.length === 0) {
				return { min: 0, max: 100 };
			}

			const globalMin = Math.min(...allValues);
			const globalMax = Math.max(...allValues);

			// Agregar padding del 10% para que las barras se vean bien
			const range = globalMax - globalMin;
			const padding = range * 0.1 || 5; // 10% de padding o mínimo 5 puntos

			return {
				min: Math.max(0, Math.floor(globalMin - padding)),
				max: Math.min(100, Math.ceil(globalMax + padding)),
			};
		},
		boxPlotOptions() {
			const categories = (this.boxPlotDataInternal && this.boxPlotDataInternal.categories) || [];
			const yAxisRange = this.boxPlotYAxisRange;

			return {
				chart: {
					type: 'boxPlot',
					height: 400,
					fontFamily: 'inherit',
					toolbar: { show: false },
					background: 'transparent',
				},
				colors: [CHART_COLORS.primary],
				plotOptions: {
					boxPlot: {
						colors: {
							upper: CHART_COLORS.primary,
							lower: CHART_COLORS.accent,
						},
					},
				},
				stroke: {
					colors: [CHART_COLORS.info],
				},
				xaxis: {
					categories,
					labels: {
						rotate: -45,
						trim: true,
						maxHeight: 80,
						hideOverlappingLabels: false,
						style: {
							fontSize: '10px',
							fontFamily: 'inherit',
							fontWeight: 500,
							colors: CHART_COLORS.info,
						},
					},
					title: {
						text: 'Competencias',
						style: {
							fontSize: '12px',
							fontWeight: 600,
							color: CHART_COLORS.info,
						},
					},
				},
				yaxis: {
					min: yAxisRange.min,
					max: yAxisRange.max,
					labels: {
						formatter: val => `${val.toFixed(1)}%`,
						style: {
							fontSize: '11px',
							colors: CHART_COLORS.info,
						},
					},
					title: {
						text: 'Puntuación (%)',
						style: {
							fontSize: '12px',
							fontWeight: 600,
							color: CHART_COLORS.info,
						},
					},
					tooltip: {
						enabled: true,
						theme: 'light',
						style: {
							fontSize: '11px',
							fontFamily: 'inherit',
						},
						y: {
							formatter: val => `${Number(val).toFixed(1)}%`,
						},
					},
				},
				grid: {
					borderColor: '#f1f5f9',
				},
				legend: {
					show: true,
					position: 'bottom',
					fontSize: '11px',
					fontWeight: 600,
				},
			};
		},
		// ============================================================
		// COMPUTED PROPERTIES FOR dataGraphics
		// ============================================================
		hasDataGraphics() {
			return this.dataGraphics && this.dataGraphics.length > 0;
		},
		validDataGraphics() {
			if (!this.dataGraphics || this.dataGraphics.length === 0) {
				return [];
			}

			// Filtrar gráficos válidos con datos numéricos válidos
			const result = this.dataGraphics
				.map((graphic) => {
					// Validar estructura básica
					if (!graphic || typeof graphic !== 'object' || !graphic.type) {
						return null;
					}

					// Validar series
					if (!graphic.series || !Array.isArray(graphic.series) || graphic.series.length === 0) {
						return null;
					}

					// Validar cada serie - debe tener data con valores numéricos
					const validSeries = graphic.series.map((serie) => {
						if (!serie || !serie.data || !Array.isArray(serie.data)) {
							return null;
						}

						// Filtrar solo valores numéricos válidos
						const cleanData = serie.data.filter(val =>
							typeof val === 'number' && !isNaN(val) && isFinite(val),
						);

						// Si no hay datos válidos, retornar null
						if (cleanData.length === 0) {
							return null;
						}

						return {
							name: serie.name || 'Serie',
							data: cleanData,
						};
					}).filter(s => s !== null);

					// Si no hay series válidas, retornar null
					if (validSeries.length === 0) {
						return null;
					}

					// Retornar el gráfico con las series validadas
					return {
						type: graphic.type,
						series: validSeries,
						categories: graphic.categories || [],
					};
				})
				.filter(g => g !== null);

			return result;
		},
		graphicModalTitle() {
			if (!this.dataGraphicByModal || !this.dataGraphicByModal.type) {
				return 'Gráfico';
			}
			// Usar el título de la sección si está disponible, de lo contrario usar el título genérico
			if (this.dataGraphicByModal.sectionTitle) {
				return this.dataGraphicByModal.sectionTitle;
			}
			const titles = {
				bar: 'Gráfico de Barras',
				singlebar: 'Gráfico de Barras Individual',
				area: 'Gráfico de Área',
				line: 'Gráfico de Línea',
				radar: 'Gráfico Radial',
				boxPlot: 'Análisis Estadístico - Box Plot',
			};
			return titles[this.dataGraphicByModal.type] || 'Gráfico Detallado';
		},
	},
	watch: {
		// Watch for dataGraphics changes
		dataGraphics: {
			handler() {
				// Data changed - computed properties will react automatically
			},
			immediate: true,
			deep: true,
		},
	},
	methods: {
		loadCompetences() {
			return this.dataEvaluation.reduce((acc, item) => {
				const acum = [...acc];
				const find = acum.find(f => f.competenceName === item.competenceName);
				if (find) {
					const countFound = find.count;
					find.count = countFound + 1;
				} else {
					acum.push({
						competenceName: item.competenceName,
						count: 1,
					});
				}
				return acum;
			}, []).sort((a, b) => {
				const nameA = a.competenceName.toLowerCase();
				const nameB = b.competenceName.toLowerCase();
				if (nameA < nameB) {
					return -1;
				}
				if (nameA > nameB) {
					return 1;
				}
				return 0;
			});
		},
		loadCompetencesAlternatives() {
			return this.dataEvaluation.reduce((acc, item) => {
				const acum = [...acc];
				const index = acum.findIndex(f => f.alternative === item.alternative &&
					f.competenceName === item.competenceName);
				if (index !== -1) {
					const count1 = acum[index].count;
					acum[index].count = count1 + 1;
				} else {
					acum.push({
						alternative: item.alternative,
						competenceName: item.competenceName,
						count: 1,
					});
				}
				return acum;
			}, []).sort((a, b) => b.count - a.count);
		},
		calculateStatistics(dataEv, key) {
			return dataEv.reduce((acc, item) => {
				const acum = [...acc];
				const index = acum.findIndex(f => f[key] === item[key]);
				if (index !== -1) {
					const count1 = acum[index].count;
					acum[index].count = count1 + 1;
					const add = acum[index].add;
					acum[index].add = add + item.avg;
					acum[index].avg = acum[index].add / acum[index].count;
				} else {
					acum.push({
						[key]: item[key],
						count: 1,
						add: item.avg,
						avg: item.avg,
						gender: item.gender,
					});
				}
				return acum;
			}, []);
		},
		jobCountsArray() {
			const jobCountMap = this.dataEvaluation.reduce((acc, item) => {
				acc[item.job] = (acc[item.job] || 0) + 1;
				return acc;
			}, {});
			return Object.entries(jobCountMap).map(([job, count]) => ({ job, count }));
		},
		colaboratorsCount() {
			return this.dataEvaluation.map(item => ({
				evaluationGroup: item.evaluationGroup,
				alternative: item.alternative,
			}));
		},
		makeDataRadar(array) {
			return array.map((item) => {
				const total = item.total;
				const optionsWithPorcentage = item.options.map((option) => {
					const porcentage = (option.count / total) * 100;
					return {
						...option,
						series: [roundTo(porcentage, 2)],
						categories: [option.alternative],
					};
				});
				return { ...item, options: optionsWithPorcentage };
			});
		},
		async loadData() {
			bus.$emit(MAIN_LOADER_EVENT, { show: true, message: 'Cargando Información...' });
			const effectiveProcessId = this.processId || this.$route.params.processId;
			const effectivePollId = this.pollId || this.$route.params.pollId;

			if (!effectiveProcessId || !effectivePollId) {
				bus.$emit(MAIN_LOADER_EVENT, { show: false });
				this.loading = false;
				return;
			}

			const body = {
				pollId: Number(effectivePollId),
				processId: Number(effectiveProcessId),
			};
			if (this.groupIds && this.groupIds.length > 0) {
				body.groupIds = this.groupIds.map(gi => gi.id);
			}
			// NOTA: subsidiaryIds NO se envía a la API, se filtra localmente después
			const requests = [
				this.$httpPraktiko.get('groups'),
				this.$httpPraktiko.get('subsidiaries'),
				this.$httpPraktiko.post('dashboard/evaluation', body),
			];
			try {
				this.loading = true;
				const [groupsResponse, subsidiariesResponse, dataResponse] = await Promise.all(requests);
				this.groups = groupsResponse.data;
				this.subsidiaries = subsidiariesResponse.data;
				this.dataEvaluation = dataResponse.data;

				// Aplicar filtro de grupo localmente
				if (this.groupIds && this.groupIds.length > 0) {
					const areaName = this.groupIds.map(a => a.name);
					this.dataEvaluation = this.dataEvaluation.filter(item => areaName.includes(item.area));
				}
				// Aplicar filtro de subsidiaria localmente
				if (this.subsidiaryIds && this.subsidiaryIds.length > 0) {
					const subsidiaryName = this.subsidiaryIds.map(a => a.name);
					this.dataEvaluation = this.dataEvaluation
						.filter(item => subsidiaryName.includes(item.subsidiary));
				}

				this.evaluators = this.calculateStatistics(this.dataEvaluation, 'evaluator');
				this.colaborators = this.calculateStatistics(this.dataEvaluation, 'colaborator');
				this.maleFemale = {
					male: this.colaborators.filter(f => f.gender === 'Masculino'),
					female: this.colaborators.filter(f => f.gender === 'Femenino'),
				};
				this.maleFemale.percentMale =
					roundTo((this.maleFemale.male.length * 100) / this.colaborators.length, 2);
				this.maleFemale.percentFemale =
					roundTo((this.maleFemale.female.length * 100) / this.colaborators.length, 2);

				const sortCompetences = this.loadCompetences().sort((a, b) => b.count - a.count);
				const dataCompetences = {
					type: 'line',
					series: [
						{
							name: 'Competencias',
							data: this.loadCompetences().map(item => item.count),
						},
					],
					categories: this.loadCompetences().map(item => item.competenceName),
				};
				this.totalCompetences = {
					type: 'bar',
					series: [
						{
							name: 'Competencias',
							data: sortCompetences.map(item => item.count),
						},
					],
					categories: sortCompetences.map(item => item.competenceName),
				};

				const alternativePriority = {
					'Muy desarrollado': 0,
					Desarrollado: 1,
					'Medianamente desarrollado': 2,
					'Poco Desarrollado': 3,
				};
				const uniqueCompetences =
					[...new Set(this.loadCompetencesAlternatives().map(obj => obj.competenceName))];
				const alternatives = this.loadCompetencesAlternatives().sort((a, b) =>
					alternativePriority[a.alternative] - alternativePriority[b.alternative]);
				const uniqueAlternatives =
					[...new Set(alternatives.map(obj => obj.alternative))];
				const competencesAlternatives = {
					type: 'area',
					series: uniqueAlternatives.reduce((acc, ua) => {
						const dataN = [];
						uniqueCompetences.forEach((uc) => {
							const itemN = this.loadCompetencesAlternatives().find(item =>
								item.competenceName === uc && item.alternative === ua);
							if (itemN) {
								dataN.push(itemN.count);
							} else {
								dataN.push(0);
							}
						});
						acc.push({ name: ua, data: dataN });
						return acc;
					}, []),
					categories: uniqueCompetences,
				};
				this.dataGraphics = [
					dataCompetences,
					competencesAlternatives,
				];

				const dataJobs = this.jobCountsArray().sort((a, b) => a.count - b.count);
				this.jobsInProcess = {
					type: 'singlebar',
					series: [
						{
							data: dataJobs.map(j => j.count),
							name: 'Puestos de Trabajo',
						},
					],
					categories: dataJobs.map(j => j.job),
				};

				const competenciesFilter = this.colaboratorsCount();
				const order = [
					'Muy desarrollado',
					'Desarrollado',
					'Medianamente desarrollado',
					'Poco desarrollado',
				];
				const uniqueEvaluationGroups =
					[...new Set(competenciesFilter.map(item => item.evaluationGroup))];
				const uniqueAlternativesByGroup =
					[...new Set(competenciesFilter.map(item => item.alternative)
						.sort((a, b) => order.indexOf(a) - order.indexOf(b)))];
				const resultDataRadar = uniqueEvaluationGroups.map((group) => {
					const optionsCount = uniqueAlternativesByGroup
						.map(option => ({ type: 'radar', alternative: option, count: 0 }));
					competenciesFilter.forEach((item) => {
						if (item.evaluationGroup === group) {
							const optionIndex = uniqueAlternativesByGroup
								.findIndex(option => option === item.alternative);
							if (optionIndex !== -1) {
								optionsCount[optionIndex].count += 1;
							}
						}
					});
					return { name: group, options: optionsCount };
				});
				const resultWithTotal = resultDataRadar.map((item) => {
					const total = item.options.reduce((sum, option) => sum + option.count, 0);
					return { ...item, total };
				});
				this.dataRadarChart = this.makeDataRadar(resultWithTotal);

				// Calcular boxPlotData con los datos filtrados
				this.boxPlotDataInternal = this.calculateBoxPlotData();

				// Forzar actualización de propiedades computadas después de todos los cálculos
				this.$nextTick(() => {
					this.$forceUpdate();
				});
			} catch (error) {
				// Error handling: silently fail and leave data empty
			} finally {
				bus.$emit(MAIN_LOADER_EVENT, { show: false });
				this.loading = false;
			}
		},
		loadDashboardData() {
			this.loadData();
		},
		refreshData() {
			this.loadData();
		},
		showGraphicModal(grafic) {
			this.isModalOpen = true;
			const enhancedGraphic = this.enhanceModalChartOptions(grafic);

			// Agregar títulos de sección si no están presentes
			if (!enhancedGraphic.sectionTitle) {
				if (enhancedGraphic.type === 'line') {
					enhancedGraphic.sectionTitle = 'Evolución de Competencias';
				} else if (enhancedGraphic.type === 'area') {
					enhancedGraphic.sectionTitle = 'Distribución por Nivel de Desarrollo';
				}
			}

			this.dataGraphicByModal = { ...enhancedGraphic };
			this.graphicModel = true;

			// Calcular altura después de que el modal se renderiza
			this.$nextTick(() => {
				this.calculateModalChartHeight();
			});

			// Recalcular una vez más para asegurar que el DOM esté actualizado
			this.$nextTick(() => {
				this.calculateModalChartHeight();
			});
		},
		enhanceModalChartOptions(graphic) {
			// Mejorar opciones del gráfico para visualización premium en modal
			if (!graphic || !graphic.options) {
				return graphic;
			}

			const baseOptions = {
				...(graphic.options.chart || {}),
				parentHeightOffset: 0,
				toolbar: { show: false },
			};

			// Grid premium con más espacio para etiquetas largas
			const baseGrid = {
				show: true,
				borderColor: '#f1f5f9',
				strokeDashArray: 0,
				padding: {
					top: 15,
					right: 60,
					bottom: 15,
					left: 20,
				},
			};

			const baseTooltip = {
				enabled: true,
				theme: 'light',
				style: {
					fontSize: '12px',
					fontFamily: 'inherit',
				},
			};

			// Crear opciones base con mejoras comunes
			const enhancedOptions = {
				chart: baseOptions,
				grid: baseGrid,
				tooltip: baseTooltip,
				...graphic.options,
			};

			// Crear gráfico mejorado
			const enhancedGraphic = { ...graphic };

			// Aplicar mejoras específicas por tipo de gráfico
			if (graphic.type === 'bar' || graphic.type === 'singlebar') {
				enhancedGraphic.options = this.enhanceBarChartOptions(enhancedOptions, graphic);
			} else if (graphic.type === 'area' || graphic.type === 'line') {
				enhancedGraphic.options = this.enhanceLineChartOptions(enhancedOptions);
			} else if (graphic.type === 'radar') {
				enhancedGraphic.options = this.enhanceRadarChartOptions(enhancedOptions);
			} else if (graphic.type === 'boxPlot') {
				enhancedGraphic.options = this.enhanceBoxPlotOptions(enhancedOptions);
			} else {
				enhancedGraphic.options = enhancedOptions;
			}

			return enhancedGraphic;
		},
		enhanceBarChartOptions(options, graphic) {
			const isHorizontal = options.plotOptions
				&& options.plotOptions.bar
				&& options.plotOptions.bar.horizontal === true;

			// Clonar options para evitar mutar el parámetro
			const enhancedOptions = {
				...options,
				xaxis: { ...options.xaxis },
				yaxis: { ...options.yaxis },
				tooltip: { ...options.tooltip },
			};

			// Configurar plotOptions premium para barras
			if (!enhancedOptions.plotOptions) {
				enhancedOptions.plotOptions = {};
			}
			if (!enhancedOptions.plotOptions.bar) {
				enhancedOptions.plotOptions.bar = {};
			}

			// Solo mejorar estilos cosméticos, preservar layout original
			if (!enhancedOptions.plotOptions.bar.borderRadius
				|| enhancedOptions.plotOptions.bar.borderRadius === 0) {
				enhancedOptions.plotOptions.bar.borderRadius = 10;
				enhancedOptions.plotOptions.bar.borderRadiusApplication = 'end';
			}
			// Preservar columnWidth y barHeight originales
			if (!enhancedOptions.plotOptions.bar.dataLabels) {
				enhancedOptions.plotOptions.bar.dataLabels = {
					enabled: false,
				};
			}

			const categories = graphic.categories || [];

			// Configurar eje X
			if (!enhancedOptions.xaxis.labels) {
				enhancedOptions.xaxis.labels = {};
			}

			if (isHorizontal) {
				// Para barras horizontales, las etiquetas están en el eje Y
				if (!enhancedOptions.yaxis) {
					enhancedOptions.yaxis = {};
				}
				if (!enhancedOptions.yaxis.labels) {
					enhancedOptions.yaxis.labels = {};
				}

				// Calcular fontSize dinámico según longitud de etiquetas
				let maxLabelLength = 0;
				if (categories && categories.length > 0) {
					maxLabelLength = Math.max(...categories.map(cat => String(cat).length));
				}

				// fontSize más pequeño para etiquetas largas
				let dynamicFontSize = '10px';
				let dynamicMaxWidth = 200;
				let dynamicPaddingLeft = 35;

				if (maxLabelLength > 40) {
					dynamicFontSize = '9px';
					dynamicMaxWidth = 220;
					dynamicPaddingLeft = 65;
				} else if (maxLabelLength > 30) {
					dynamicFontSize = '9px';
					dynamicMaxWidth = 210;
					dynamicPaddingLeft = 55;
				} else if (maxLabelLength > 20) {
					dynamicFontSize = '10px';
					dynamicMaxWidth = 200;
					dynamicPaddingLeft = 45;
				}

				// Ajustar grid padding para dar espacio a etiquetas
				if (!enhancedOptions.grid) {
					enhancedOptions.grid = {};
				}
				if (!enhancedOptions.grid.padding) {
					enhancedOptions.grid.padding = {};
				}
				enhancedOptions.grid.padding.left = dynamicPaddingLeft;
				// Asegurar paddingRight para balance del gráfico
				if (!enhancedOptions.grid.padding.right || enhancedOptions.grid.padding.right < 40) {
					enhancedOptions.grid.padding.right = 60;
				}

				// Configurar etiquetas dinámicas para eje Y
				enhancedOptions.yaxis.labels.style = {
					fontSize: dynamicFontSize,
					fontWeight: 500,
					colors: ['#334155'],
					fontFamily: 'inherit',
				};
				enhancedOptions.yaxis.labels.minWidth = 0;
				enhancedOptions.yaxis.labels.maxWidth = dynamicMaxWidth;
				enhancedOptions.yaxis.labels.offsetX = 0;
				enhancedOptions.yaxis.labels.trim = false;
				enhancedOptions.yaxis.labels.hideOverlappingLabels = false;
				// Truncar etiquetas largas a 25 caracteres, el tooltip muestra el texto completo
				enhancedOptions.yaxis.labels.formatter = function formatterLabel(val) {
					if (typeof val === 'string' && val.length > 25) {
						return `${val.substring(0, 22)}...`;
					}
					return val;
				};

				enhancedOptions.xaxis.labels.show = false;
			} else {
				// Para barras verticales, las etiquetas están en el eje X
				enhancedOptions.xaxis.labels.style = {
					fontSize: '11px',
					fontWeight: 600,
					colors: ['#334155'],
					fontFamily: 'inherit',
				};
				// Rotar labels para mejor legibilidad con nombres largos
				enhancedOptions.xaxis.labels.rotate = -45;
				enhancedOptions.xaxis.labels.trim = false;
				enhancedOptions.xaxis.labels.hideOverlappingLabels = true;
				enhancedOptions.xaxis.labels.maxHeight = 120;

				// NO truncar etiquetas en el eje X - mostrar texto completo
				// ApexCharts manejará el espacio automáticamente
			}

			enhancedOptions.xaxis.axisBorder = { show: false };
			enhancedOptions.xaxis.axisTicks = { show: false };

			// Configurar eje Y (valores)
			if (!enhancedOptions.yaxis.labels) {
				enhancedOptions.yaxis.labels = {};
			}

			if (!isHorizontal) {
				enhancedOptions.yaxis.labels.style = {
					fontSize: '10px',
					fontWeight: 600,
					colors: ['#64748b'],
					fontFamily: 'inherit',
				};
			}

			// Tooltip premium con diseño mejorado
			enhancedOptions.tooltip.y = {
				formatter: function formatterY(val, { dataPointIndex }) {
					const value = typeof val === 'number' ? val : 0;
					let label = '';
					if (categories && categories[dataPointIndex] !== undefined) {
						label = categories[dataPointIndex];
					}
					return `
						<div style="text-align: left; min-width: 180px; padding: 8px 0;">
							<div style="font-size: 12px; font-weight: 600; color: #64748b; margin-bottom: 6px;">${label}</div>
							<div style="font-size: 18px; font-weight: 700; color: #0f172a;">${value}</div>
						</div>
					`;
				},
			};
			enhancedOptions.tooltip.x = {
				show: false,
			};

			// Agregar sombra sutil a las barras
			if (!enhancedOptions.stroke) {
				enhancedOptions.stroke = {
					show: true,
					width: 0,
				};
			}

			// DataLabels solo si hay pocas categorías
			if (categories.length <= 6) {
				enhancedOptions.plotOptions.bar.dataLabels = {
					enabled: true,
					formatter: function formatterDataLabels(val) {
						return typeof val === 'number' ? val : 0;
					},
					style: {
						fontSize: '10px',
						fontWeight: 700,
						colors: ['#334155'],
					},
					dropShadow: {
						enabled: true,
						top: 1,
						left: 1,
						blur: 2,
						opacity: 0.15,
					},
				};
			}

			return enhancedOptions;
		},
		enhanceLineChartOptions(options) {
			// Clonar options para evitar mutar el parámetro
			const enhancedOptions = {
				...options,
				xaxis: { ...options.xaxis },
				yaxis: { ...options.yaxis },
			};

			// Configurar eje X con fontSize legible y sin truncado
			if (!enhancedOptions.xaxis.labels) {
				enhancedOptions.xaxis.labels = {};
			}
			enhancedOptions.xaxis.labels.style = {
				fontSize: '11px',
				fontWeight: 600,
				colors: ['#334155'],
				fontFamily: 'inherit',
			};
			enhancedOptions.xaxis.labels.rotate = -45;
			enhancedOptions.xaxis.labels.trim = false;
			enhancedOptions.xaxis.labels.hideOverlappingLabels = true;

			// NO truncar etiquetas - ApexCharts ajustará automáticamente
			enhancedOptions.xaxis.axisBorder = { show: false };
			enhancedOptions.xaxis.axisTicks = { show: false };

			// Configurar eje Y con fontSize legible
			if (!enhancedOptions.yaxis.labels) {
				enhancedOptions.yaxis.labels = {};
			}
			enhancedOptions.yaxis.labels.style = {
				fontSize: '11px',
				fontWeight: 600,
				colors: ['#64748b'],
				fontFamily: 'inherit',
			};

			return enhancedOptions;
		},
		enhanceRadarChartOptions(options) {
			// Para gráficos radiales/radar, asegurar legibilidad
			const enhancedOptions = {
				...options,
				plotOptions: { ...options.plotOptions },
				legend: { ...options.legend },
			};

			if (!enhancedOptions.plotOptions.radialBar) {
				enhancedOptions.plotOptions.radialBar = {};
			}
			if (!enhancedOptions.plotOptions.radialBar.dataLabels) {
				enhancedOptions.plotOptions.radialBar.dataLabels = {};
			}

			// Configurar etiquetas de nombre y valor
			if (!enhancedOptions.plotOptions.radialBar.dataLabels.name) {
				enhancedOptions.plotOptions.radialBar.dataLabels.name = {};
			}
			enhancedOptions.plotOptions.radialBar.dataLabels.name.fontSize = '11px';
			enhancedOptions.plotOptions.radialBar.dataLabels.name.color = '#64748b';
			enhancedOptions.plotOptions.radialBar.dataLabels.name.fontWeight = 600;

			if (!enhancedOptions.plotOptions.radialBar.dataLabels.value) {
				enhancedOptions.plotOptions.radialBar.dataLabels.value = {};
			}
			enhancedOptions.plotOptions.radialBar.dataLabels.value.fontSize = '14px';
			enhancedOptions.plotOptions.radialBar.dataLabels.value.fontWeight = 700;
			enhancedOptions.plotOptions.radialBar.dataLabels.value.color = '#0f172a';

			// Truncar labels largos
			if (enhancedOptions.labels && Array.isArray(enhancedOptions.labels)) {
				enhancedOptions.labels = enhancedOptions.labels.map((label) => {
					if (label && label.length > 18) {
						return `${label.substring(0, 15)}...`;
					}
					return label;
				});
			}

			// Configurar leyenda
			enhancedOptions.legend.position = 'bottom';
			enhancedOptions.legend.fontSize = '11px';
			enhancedOptions.legend.fontWeight = 600;
			enhancedOptions.legend.itemMargin = {
				horizontal: 8,
				vertical: 4,
			};

			return enhancedOptions;
		},
		enhanceBoxPlotOptions(options) {
			// Clonar options para evitar mutar el parámetro
			const enhancedOptions = {
				...options,
				xaxis: { ...options.xaxis },
				yaxis: { ...options.yaxis },
			};

			// Configurar eje X con fontSize legible y sin truncado
			if (!enhancedOptions.xaxis.labels) {
				enhancedOptions.xaxis.labels = {};
			}
			enhancedOptions.xaxis.labels.style = {
				fontSize: '11px',
				fontWeight: 600,
				colors: ['#334155'],
				fontFamily: 'inherit',
			};
			enhancedOptions.xaxis.labels.rotate = -45;
			enhancedOptions.xaxis.labels.trim = false;
			enhancedOptions.xaxis.labels.hideOverlappingLabels = true;

			// Configurar eje Y con fontSize legible
			if (!enhancedOptions.yaxis.labels) {
				enhancedOptions.yaxis.labels = {};
			}
			enhancedOptions.yaxis.labels.style = {
				fontSize: '11px',
				fontWeight: 600,
				colors: ['#64748b'],
				fontFamily: 'inherit',
			};

			return enhancedOptions;
		},
		calculateModalChartHeight() {
			// Calcular altura conservadora para eliminar completamente el scrollbar
			try {
				const viewportHeight = window.innerHeight;

				// El modal ocupa el 96% de la pantalla
				const modalHeight = Math.floor(viewportHeight * 0.96);

				// Restar todos los elementos fijos con margen generoso
				const headerHeight = 64;
				const bodyPaddingTop = 12;
				const bodyPaddingBottom = 12;
				const chartLabelsSpace = 50;
				const safetyMargin = 30;

				// Calcular altura del gráfico con margen de seguridad
				const availableHeight = modalHeight
					- headerHeight
					- bodyPaddingTop
					- bodyPaddingBottom
					- chartLabelsSpace
					- safetyMargin;

				// Altura mínima conservadora
				const minHeight = 350;
				this.modalChartHeight = Math.max(availableHeight, minHeight);
			} catch (error) {
				this.modalChartHeight = 450;
			}
		},
		closeModal() {
			this.graphicModel = false;
			this.isModalOpen = false;
			this.dataGraphicByModal = {};
		},
		expandCompetenceChart() {
			const chartData = {
				type: 'bar',
				series: this.competenceChartSeries,
				categories: this.totalCompetences.categories || [],
				height: 750,
				options: this.competenceChartOptions,
				sectionTitle: 'Competencias Principales',
			};
			this.showGraphicModal(chartData);
		},
		expandJobsChart() {
			const chartData = {
				type: 'bar',
				series: this.jobsChartSeries,
				categories: this.jobsChartOptions.xaxis.categories || [],
				height: 750,
				options: this.jobsChartOptions,
				sectionTitle: 'Puestos de Trabajo',
			};
			this.showGraphicModal(chartData);
		},
		expandAlternativesChart() {
			const chartData = {
				type: 'bar',
				series: this.alternativesDistributionSeries,
				categories: this.alternativesDistributionOptions.xaxis.categories || [],
				height: 750,
				options: this.alternativesDistributionOptions,
				sectionTitle: 'Distribución por Nivel de Desarrollo',
			};
			this.showGraphicModal(chartData);
		},
		expandRadarChart(group) {
			const chartData = {
				type: 'radar',
				series: group.radialSeries,
				categories: group.radialOptions.labels || [],
				height: 750,
				options: group.radialOptions,
				sectionTitle: `Grupos de Referencia - ${group.name}`,
			};
			this.showGraphicModal(chartData);
		},
		expandBoxPlotChart() {
			const chartData = {
				type: 'boxPlot',
				series: (this.boxPlotDataInternal && this.boxPlotDataInternal.series) || [],
				categories: (this.boxPlotDataInternal && this.boxPlotDataInternal.categories) || [],
				height: 750,
				options: this.boxPlotOptions,
				sectionTitle: 'Distribución por Cuartiles',
			};
			this.showGraphicModal(chartData);
		},
		calculateBoxPlotData() {
			if (!this.dataEvaluation || this.dataEvaluation.length === 0) {
				return { series: [], categories: [], hasData: false };
			}

			// Agrupar por competencia - filtrar datos inválidos
			const competenceGroups = {};
			this.dataEvaluation.forEach((evaluation) => {
				// Validar que tenga nombre de competencia y avg válido
				if (!evaluation.competenceName || evaluation.avg == null) {
					return;
				}
				if (!competenceGroups[evaluation.competenceName]) {
					competenceGroups[evaluation.competenceName] = [];
				}
				competenceGroups[evaluation.competenceName].push(evaluation.avg);
			});

			// Filtrar grupos con al menos 2 datos para cálculos estadísticos válidos
			const validGroups = Object.entries(competenceGroups)
				.filter(([, scores]) => scores.length >= 2);

			if (validGroups.length === 0) {
				return { series: [], categories: [], hasData: false };
			}

			const categories = validGroups.map(([comp]) => comp);
			const series = validGroups.map(([comp, scores]) => {
				const sorted = [...scores].sort((a, b) => a - b);
				const { q1, q2, q3 } = calculateQuartiles(sorted);
				const { min, max } = calculateMinMax(sorted);

				// Truncar nombre largo si es necesario
				const displayName = comp.length > 25 ? `${comp.substring(0, 22)}...` : comp;

				// Validar que todos los valores sean números válidos
				const validMin = typeof min === 'number' && !isNaN(min) ? min : 0;
				const validQ1 = typeof q1 === 'number' && !isNaN(q1) ? q1 : 0;
				const validQ2 = typeof q2 === 'number' && !isNaN(q2) ? q2 : 0;
				const validQ3 = typeof q3 === 'number' && !isNaN(q3) ? q3 : 0;
				const validMax = typeof max === 'number' && !isNaN(max) ? max : 0;

				return {
					x: displayName,
					y: [validMin, validQ1, validQ2, validQ3, validMax],
				};
			});

			// Validar que series sea un array válido
			const validSeries = Array.isArray(series) ? series : [];
			return { series: [{ data: validSeries }], categories, hasData: true };
		},
		getFilter(type, value) {
			if (type === 'group') {
				this.groupIds = value;
			} else if (type === 'subsidiary') {
				this.subsidiaryIds = value;
			}
			this.loadData();
		},
		goToBack() {
			const effectiveProcessId = this.processId || this.$route.params.processId;
			this.goTo('report-evaluation', { params: { id: effectiveProcessId } });
		},
	},
	mounted() {
		this.loadDashboardData();
	},
};
</script>

<style lang="scss" scoped>
.custom-dashboard-v2 {
	padding: 0;
	background: #f8fafc;
	min-height: 100vh;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

// ============================================
// HEADER STYLES
// ============================================
.dashboard-header {
	padding: 1rem 1.5rem;
	margin-top: 3rem;
	// Compensar el header fijo del sistema
	position: relative;
	z-index: 10;
	// Centrar como el resto de la interfaz
	max-width: 1400px;
	margin-left: auto;
	margin-right: auto;
}

.header-layout {
	padding: 0;
	margin: 0;
	min-height: 36px;
}

.filters-layout {
	flex: 1;
	gap: 1rem;
	align-items: center;
}

.filter-item {
	padding: 0;
	min-width: 200px;
	max-width: 280px;
}

.filter-label {
	display: block;
	width: 100%;
	margin-bottom: 0.25rem;
	font-size: 0.6875rem;
	font-weight: 600;
	line-height: 1.2;
	letter-spacing: 0.025em;
	text-transform: uppercase;
	color: inherit;
}

// Estilos compactos y modernos para los selects
.select-compact {
	&::v-deep .v-input__control {
		min-height: 28px !important;
	}

	&::v-deep .v-input__slot {
		min-height: 28px !important;
		font-size: 0.75rem;
		border-radius: 4px;
	}

	&::v-deep .v-select__selections {
		min-height: 28px !important;
		font-size: 0.75rem;
		padding: 1px 6px;
	}

	&::v-deep .v-label {
		font-size: 0.75rem;
		top: 2px;
	}

	&::v-deep .v-icon {
		font-size: 14px;
		width: 14px;
		height: 14px;
	}

	&::v-deep .v-chip {
		font-size: 0.6875rem;
		height: 18px;
		border-radius: 3px;
		margin: 1px;
		padding: 0 4px;
	}
}

.wrapper-btns {
	display: flex;
	align-items: center;
	padding: 0;
}

.back-btn {
	min-width: 80px;
	height: 28px;
	margin: 0;
	font-size: 0.6875rem;
	font-weight: 500;
	padding: 0 10px;
	letter-spacing: 0.025em;
	transition: all 0.2s ease;
	border-radius: 4px;
	display: inline-flex;
	align-items: center;
	gap: 4px;

	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
	}
}

.back-btn-icon {
	width: 12px !important;
	height: 12px !important;
	object-fit: contain;
	transition: transform 0.2s ease;
	opacity: 0.9;
	display: block;
}

.back-btn:hover .back-btn-icon {
	transform: translateX(-1px);
	opacity: 1;
}

.back-btn-text {
	font-size: 0.6875rem;
	font-weight: 500;
	line-height: 1;
}

// Responsive adjustments
@media (max-width: 960px) {
	.dashboard-header {
		padding: 1rem;
	}

	.filter-item {
		min-width: 180px;
		max-width: 240px;
	}

	.wrapper-btns {
		padding: 0;
	}
}

@media (max-width: 600px) {
	.dashboard-header {
		padding: 1rem;
		margin-top: 0.25rem;
	}

	.header-layout {
		flex-direction: column;
		align-items: stretch;
		gap: 0.75rem;
	}

	.filters-layout {
		flex-direction: column;
		width: 100%;
		gap: 0.75rem;
	}

	.filter-item {
		min-width: 100%;
		max-width: 100%;
		padding: 0;
	}

	.wrapper-btns {
		justify-content: flex-start;
		padding: 0;
	}

	.back-btn {
		min-width: 70px;
		height: 26px;
		font-size: 0.625rem;
		padding: 0 8px;
		gap: 3px;
	}

	.back-btn-icon {
		width: 10px !important;
		height: 10px !important;
	}

	.back-btn-text {
		font-size: 0.625rem;
	}
}

// ============================================
// KPI SECTION
// ============================================
.kpi-section {
	padding: 1.5rem;
	background: linear-gradient(to right, #b45309, #f09c2b);
}

.kpi-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 1rem;
	max-width: 1400px;
	margin: 0 auto;
}

.kpi-card {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 16px;
	padding: 1.25rem;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	transition: transform 0.3s ease, box-shadow 0.3s ease;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1rem;
	}

	&__icon {
		width: 48px;
		height: 48px;
		border-radius: 12px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.3));
		color: white;

		svg {
			width: 24px;
			height: 24px;
		}
	}

	&__trend {
		font-size: 0.75rem;
		font-weight: 600;
	}

	&__content {
		text-align: center;
		position: relative;
		z-index: 1;
	}

	&__label {
		font-size: 0.75rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: #64748b;
		margin: 0 0 0.5rem 0;
		position: relative;
		z-index: 3;
	}

	&__label-inline {
		font-size: 0.875rem;
		font-weight: 700;
		color: #1e293b;
		margin: 0;
		position: relative;
		z-index: 3;
	}

	&__value {
		font-size: 2rem;
		font-weight: 800;
		color: #0f172a;
		margin: 0 0 0.75rem 0;
		line-height: 1;
		position: relative;
		z-index: 2;
	}

	&__progress {
		height: 4px;
		background: linear-gradient(90deg, #f09c2b, #fbbf24);
		border-radius: 2px;
		overflow: hidden;
		display: flex;
		align-items: center;
		position: relative;
		z-index: 1;
		margin-top: 0.5rem;
		transition: width 1s ease;

		&--success {
			background: linear-gradient(90deg, #10b981, #34d399);
		}
	}

	&__donut {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
		padding: 0.5rem 0;
	}

	&--expanded {
		grid-column: span 2;
		@media (max-width: 768px) {
			grid-column: span 1;
		}
	}
}


.trend-indicator {
	padding: 0.25rem 0.5rem;
	border-radius: 20px;
	font-size: 0.7rem;
	font-weight: 700;

	&--up {
		background: #dcfce7;
		color: #15803d;
	}
}

.donut-legend {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.legend-item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.75rem;
	font-weight: 600;
	color: #475569;
}

.legend-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;

	&--male {
		background: #f09c2b;
	}

	&--female {
		background: #ec4899;
	}
}

// ============================================
// CHARTS GRID
// ============================================
.charts-grid {
	display: grid;
	gap: 1.5rem;
	padding: 1.5rem;
	max-width: 1400px;
	margin: 0 auto;
	grid-template-columns: repeat(2, 1fr);

	@media (max-width: 991px) {
		grid-template-columns: 1fr;
	}

	@media (max-width: 576px) {
		padding: 1rem;
		gap: 1rem;
	}
}

.chart-card {
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	transition: box-shadow 0.3s ease;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1.25rem 1.5rem;
		border-bottom: 1px solid #f1f5f9;

		&--compact {
			padding: 0.875rem 1.25rem;
		}
	}

	&__title {
		font-size: 1rem;
		font-weight: 700;
		color: #0f172a;
		margin: 0;
	}

	&__badge {
		padding: 0.25rem 0.75rem;
		background: #dbeafe;
		color: #1d4ed8;
		font-size: 0.7rem;
		font-weight: 700;
		border-radius: 20px;
	}

	&__actions {
		display: flex;
		gap: 0.5rem;
	}

	&__body {
		padding: 1rem 1.5rem 1.5rem;

		&--full {
			padding: 0.5rem 1rem 0.5rem;
			height: auto;
			min-height: 150px;
			display: block;
		}
	}

	&--large {
		grid-column: span 2;
		@media (max-width: 1200px) {
			grid-column: span 1;
		}
	}

	&--main {
		// Main charts (Competencias and Puestos) - occupy 1 column in 2-column grid
		grid-column: span 1;

		@media (max-width: 991px) {
			grid-column: span 1;
		}
	}

	&--wide {
		grid-column: span 2;
		@media (max-width: 1200px) {
			grid-column: span 1;
		}
	}

	&--full {
		grid-column: 1 / -1;
	}

	&--medium {
		@media (min-width: 1400px) {
			grid-column: span 1;
		}
		@media (min-width: 992px) and (max-width: 1399px) {
			grid-column: span 1;
		}
	}
}

// ============================================
// RADAR SECTION
// ============================================
.radar-section {
	padding: 1.5rem;
	max-width: 1400px;
	margin: 0 auto;
}

.section-header {
	margin-bottom: 1.5rem;
}

.section-title {
	font-size: 1.5rem;
	font-weight: 800;
	color: #0f172a;
	margin: 0 0 0.25rem 0;
}

.section-subtitle {
	font-size: 0.875rem;
	color: #64748b;
	margin: 0;
}

.radar-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1.5rem;

	@media (max-width: 1200px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
}

.radar-card {
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	transition: box-shadow 0.3s ease, transform 0.3s ease;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1.25rem 1.5rem;
		background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
		border-bottom: 1px solid #e2e8f0;
	}

	&__title {
		font-size: 0.875rem;
		font-weight: 700;
		color: #0f172a;
		margin: 0;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	&__badge {
		padding: 0.25rem 0.75rem;
		background: #dbeafe;
		color: #1d4ed8;
		font-size: 0.7rem;
		font-weight: 700;
		border-radius: 20px;
	}

	&__body {
		padding: 1.5rem;
		display: flex;
		justify-content: center;
	}

	&__footer {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		padding: 1rem 1.5rem;
		background: #f8fafc;
		border-top: 1px solid #e2e8f0;
	}
}

.competency-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 0.75rem;

	&__label {
		color: #64748b;
		font-weight: 500;
	}

	&__value {
		color: #0f172a;
		font-weight: 700;
	}
}

// ============================================
// UTILITY
// ============================================
.btn-icon {
	background: transparent;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
	color: #64748b;

	&:hover:not(:disabled) {
		background: #f1f5f9;
		border-color: #cbd5e1;
		color: #0f172a;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

.spin {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

// ============================================
// RESPONSIVE
// ============================================
@media (max-width: 768px) {
	.kpi-grid {
		grid-template-columns: 1fr;
	}

	.charts-grid {
		grid-template-columns: 1fr;
		padding: 1rem;
	}

	.kpi-card {
		&--expanded {
			grid-column: span 1;
		}
	}
}

// ============================================
// NEW SECTIONS STYLES
// ============================================

.stats-section,
.no-data-section {
	padding: 1.5rem;
	max-width: 1600px;
	margin: 0 auto;
}

.section-header {
	margin-bottom: 1.5rem;
}

.section-title {
	font-size: 1.5rem;
	font-weight: 800;
	color: #0f172a;
	margin: 0 0 0.25rem 0;
}

.section-subtitle {
	font-size: 0.875rem;
	color: #64748b;
	margin: 0;
}

// ============================================
// RADAR CARD FULL WIDTH
// ============================================

.radar-card--full {
	grid-column: 1 / -1;
}

@media (max-width: 768px) {
	.radar-card--full {
		grid-column: span 1;
	}
}

// ============================================
// COMPETENCIES SECTION STYLES
// ============================================

.competencies-section {
	padding: 1.5rem;
	max-width: 1600px;
	margin: 0 auto;
}

.competencies-grid {
	display: grid;
	gap: 1.5rem;
	grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
		padding: 1rem;
		gap: 1rem;
	}
}

.competency-card {
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	transition: box-shadow 0.3s ease, transform 0.3s ease;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);
	}

	&--full {
		grid-column: 1 / -1;
	}
}

// ============================================
// EMPTY STATE STYLES
// ============================================

.chart-card__body--empty {
	min-height: 250px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 1rem;
	padding: 3rem 2rem;
	text-align: center;
}

.empty-icon {
	width: 64px;
	height: 64px;
	opacity: 0.5;
}

.empty-text {
	font-size: 1.125rem;
	font-weight: 600;
	color: #64748b;
	margin: 0;
}

.empty-subtext {
	font-size: 0.875rem;
	color: #94a3b8;
	margin: 0;
	max-width: 400px;
}

@media (max-width: 576px) {
	.chart-card__body--empty {
		padding: 2rem 1rem;
		min-height: 200px;
	}

	.empty-icon {
		width: 48px;
		height: 48px;
	}

	.empty-text {
		font-size: 1rem;
	}

	.empty-subtext {
		font-size: 0.8125rem;
	}
}
</style>

// ============================================
// MODAL STYLES (Non-scoped for Vuetify v-dialog)
// ============================================
<style lang="scss">
// Modal Extra-Large - Sin scrollbar con gráfico perfecto
.graphic-modal-premium.v-dialog {
	::v-deep .v-dialog__content {
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
	}
}

.graphic-modal-premium {
	.p-card {
		max-height: 94vh !important;
		display: flex !important;
		flex-direction: column !important;
		overflow: hidden !important;
		border-radius: 16px !important;
		box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4) !important;
		background: #ffffff !important;
	}

	.p-header-dialog {
		flex-shrink: 0 !important;
		border-bottom: 1px solid #e2e8f0 !important;
		padding: 1rem 1.5rem !important;
		background: #f8fafc !important;
	}

	.p-card-body {
		display: none !important;
	}

	.graphic-modal-body-premium {
		display: flex !important;
		flex-direction: column !important;
		flex: 1 !important;
		min-height: 0 !important;
		padding: 0.5rem 1.5rem 0.5rem 1.5rem !important;
		overflow: hidden !important;
		background: #ffffff !important;
	}

	.competency-chart-expanded {
		width: 100% !important;
		height: 100% !important;
		display: flex !important;
		flex-direction: column !important;
		overflow: hidden !important;

		&__body {
			width: 100% !important;
		flex: 1 !important;
		display: flex !important;
		flex-direction: column !important;
		padding: 0 !important;
		min-height: 0 !important;
			overflow: hidden !important;
		}
	}

	// Asegurar que el gráfico de ApexCharts use todo el espacio sin scroll
	.apexcharts-canvas,
	.apexcharts-svg,
	.apexcharts-html,
	.apexcharts-inner {
		width: 100% !important;
		height: 100% !important;
		display: block !important;
		overflow: visible !important;
	}

	.apexcharts-bar-series {
		path {
			filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
		}
	}

	.apexcharts-tooltip {
		box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15) !important;
		border-radius: 12px !important;
		border: 1px solid #e2e8f0 !important;
	}

	.apexcharts-tooltip-title {
		background: #f8fafc !important;
		border-bottom: 1px solid #e2e8f0 !important;
		font-family: inherit !important;
		font-weight: 600 !important;
	}

	.apexcharts-text {
		font-family: inherit !important;
	}
}

// Responsive para tablets
@media (max-width: 1200px) {
	.graphic-modal-premium {
		.p-card {
			max-height: 93vh !important;
		}
	}
}

// Responsive para móviles
@media (max-width: 768px) {
	.graphic-modal-premium {
		.p-card {
			max-height: 100vh !important;
			border-radius: 0 !important;
		}

		.p-header-dialog {
			padding: 0.875rem 1rem !important;
		}

		.graphic-modal-body-premium {
			padding: 0.5rem 1rem 0.5rem 1rem !important;
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/modules/evaluation-360/components/dashboard/custom-dashboard-v2.vue